/**
 * QBlock — the `?`-block (Brief 009).
 *
 * A SOLID, tile-aligned 1×1 entity. A head-bonk from below (player moving up, World's
 * solid-resolution reports a BOTTOM hit) pops its reward into the cell above, swaps to
 * the used sprite, plays a bump-nudge, and goes inert (a plain solid). When already
 * `spent` (authored or after one bonk) it is an inert solid — no further rewards.
 *
 * Determinism: the reward emission is seedless (ctx.spawnEntityAt derives px from
 * integer tile math), so a replay re-pops identically. The bonk-nudge is render-ONLY
 * (renderYOffset) and never touches body/collision — the brief's "wobble is visual-
 * only" rule. floor/min/abs arithmetic only → ESLint-clean under src/engine/.
 */
import type { Entity, EntityContext } from '../Entity';
import { EntityType } from '../LevelFormat';
import type { AABB } from '../TileGrid';
import { resolveRewardToken } from './rewards';

/** Seconds the bonk-nudge plays (render-only). */
const BUMP_SECONDS = 0.18;
/** Peak upward nudge of the bonk (px, render-only). */
const BUMP_RISE = 10;
/** `?` shimmer flip interval (seconds per frame, render-only). */
const SHIMMER_INTERVAL = 0.35;

export class QBlock implements Entity {
  readonly type = EntityType.QBLOCK;
  body: AABB;
  vx = 0;
  vy = 0;
  facing: 1 | -1 = 1;
  alive = true;

  private spent: boolean;
  private reward: string;
  private rewardCount: number;
  private bumpTimer = 0;   // render-only bonk nudge
  private shimmer = 0;     // render-only 2-frame `?` shimmer phase

  /**
   * @param cx     spawn center-x (world px)
   * @param feetY  bottom of the block's tile cell (world px)
   * @param props  `reward` (default 'COIN'), `count` (default 1), `spent` (default false)
   * @param ts     tile size — the block fills exactly one ts² cell
   */
  constructor(cx: number, feetY: number, props: Record<string, unknown> | undefined, ts: number) {
    this.body = { x: cx - ts / 2, y: feetY - ts, w: ts, h: ts };
    this.spent = props?.spent === true;
    this.reward = typeof props?.reward === 'string' ? props.reward : 'COIN';
    const c = props?.count;
    this.rewardCount = typeof c === 'number' && c > 0 ? Math.floor(c) : 1;
  }

  /** Always solid (inert once spent). */
  solidBody(): AABB { return this.body; }

  update(dt: number, _ctx: EntityContext): void {
    this.shimmer += dt;
    if (this.bumpTimer > 0) this.bumpTimer = Math.max(0, this.bumpTimer - dt);
  }

  onBonk(ctx: EntityContext): void {
    if (this.spent) return; // inert solid — no-op when empty
    this.spent = true;
    this.bumpTimer = BUMP_SECONDS;
    // Pop the reward into the cell ABOVE (its feetY = this block's top edge). COIN-only
    // in 009; 010 widens `reward` to item ids via the shared reward-token map (passes
    // props.kind so a single ITEM type carries the pickup variant).
    const { type, props } = resolveRewardToken(this.reward);
    const cx = this.body.x + this.body.w / 2;
    for (let i = 0; i < this.rewardCount; i++) {
      ctx.spawnEntityAt(type, cx + i * 10, this.body.y, props);
    }
    ctx.playSound(this.reward === 'COIN' ? 'coin' : 'bump', 0.5);
  }

  /** Solid block — interaction is the head-bonk, never overlap. */
  onPlayerOverlap(_ctx: EntityContext): void { /* no-op */ }

  spriteKey(): string {
    if (this.spent) return 'block_used';
    return Math.floor(this.shimmer / SHIMMER_INTERVAL) % 2 === 0 ? 'block_q' : 'block_q_alt';
  }

  visible(): boolean { return true; }

  renderYOffset(): number {
    if (this.bumpTimer <= 0) return 0;
    // Linear ease (no transcendentals): -BUMP_RISE at the hit, settling to 0.
    return -BUMP_RISE * (this.bumpTimer / BUMP_SECONDS);
  }
}
